<template>
  <DraftMain
    :panelTitle="panelTitle"
    :initialValues="initialValues"
    :confirmDeletionFn="confirmDeletionFn"
    :closeModal="closeModal"
    :onSearch="onSearch"
    :showModal="showModal"
  >
    <template #searchHeadOperation>
      <div class="flex items-center justify-center gap-2">
        <FilledButton
          class="flex items-center justify-center px-5 py-1.5 gap-2 text-white border-primary-700 border-2"
          @click.prevent="onPay()"
        >
          <i class="fa fa-credit-card text-white"></i>
          {{ $t('Pay') }}
        </FilledButton>
        <FilledButton
          class="flex items-center justify-center px-5 py-1.5 gap-2 text-white border-gray-500 border-2"
          fillClass="bg-gray-500"
          @click.prevent="openModal()"
        >
          <i class="fa fa-trash text-white"></i>
          {{ $t('Delete') }}
        </FilledButton>
      </div>
    </template>
    <template #searchPanel="{ values }">
      <DraftSearchPanel
        :initialValues="initialValues"
        isShowFingerprintCheckbox="true"
        :roleType="currentRole"
        :values="values"
      />
    </template>
    <template #searchResult>
      <DraftTable
        :isLoading="isLoading"
        :tableHeaders="tableHeaders"
        :tableData="tableData"
        :initialValues="initialValues"
        :pageLength="pageLength"
        @onPageClick="onPageClick"
        @selected="selectedNumberOfRow"
        @checked="selectedRow"
        @checkOut="checkOut"
        @checkIn="checkIn"
        @delete="deleteSelected"
        ref="draftTableRef"
      >
        <template #tableRow="{ request }">
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.requestNumber }}
            <div v-if="request.withException">{{ $t('withException') }}</div>
            <div v-if="isNotEligableExternalSystemToTakeAction(request)">
              {{ $t('submitedFromExternalSystem') }}
            </div>
          </td>
          <td
            class="min-w-[180px] px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <span v-html="checkOwnerName(request)"></span>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.serviceTransactionName }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ formattedCreationDate(request?.creationDate) }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.draftStatus.text }}
          </td>
        </template>
        <template #extendRow="{ request }">
          <ReadyPayAppendRow
            :requestDraft="request"
            :genderText="getGenderText(request.request?.genderId)"
          />
        </template>
      </DraftTable>
    </template>
  </DraftMain>
</template>
<script setup>
import { onBeforeMount, ref } from 'vue'
import { notify } from 'notiwind'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import FilledButton from '@/components/Buttons/Filled.vue'
import DraftMain from '@/views/individual/draft/Main.vue'
import DraftTable from '@/views/individual/draft/DraftTable.vue'
import DraftSearchPanel from '@/views/individual/draft/DraftSearchPanel.vue'
import ReadyPayAppendRow from '@/views/individual/draft/ReadyPayAppendRow.vue'
import apiHelperService from '@/services'
import lookups from '@/assets/json/clientLookups.json'
import { useMultiplePayStore } from '@/stores/multiplePay'
import { useAuthStore } from '@/stores/auth'
import useFormatDate from '@/composables/useFormatDate'

const multiplePayStore = useMultiplePayStore()
const route = useRoute()
const router = useRouter()
const i18n = useI18n()
const { isMoreThanXDays } = useFormatDate()

const draftId = ref([])
const showModal = ref(false)
const panelTitle = ref('draftsReadyToPayment')
const isLoading = ref(false)
const initialValues = ref({
  startDate: '',
  endDate: '',
  currentPage: 1,
  pageSize: 10,
  requireEnrollmentInd: false
})

const pageLength = ref(10)
const tableData = ref([])
const draftStatusId = ref('')
const authStore = useAuthStore()
const currentRole = ref()
const draftTableRef = ref()
const currentSearchValues = ref()

const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('RequestNumber')
  },
  {
    value: 3,
    label: i18n.t('Beneficiarysname')
  },
  {
    value: 4,
    label: i18n.t('RequestType')
  },
  {
    value: 5,
    label: i18n.t('CreationDate')
  },
  {
    value: 6,
    label: i18n.t('DraftStatus')
  }
])

currentRole.value = authStore.isUserEstablishment ? 'establishment' : 'individual'

const closeModal = () => {
  showModal.value = false
}

const isNotEligableExternalSystemToTakeAction = (requestDraft) => {
  const isVisaPlatform =
    requestDraft?.request?.lookupExternalSystemId == lookups.externalSystem.visaPlatform
  return isVisaPlatform
}

const checkOwnerName = (request) => {
  return draftTableRef.value?.checkOwnerName(request)
}

const formattedCreationDate = (request) => {
  return draftTableRef.value?.formattedCreationDate(request)
}
const getGenderText = (genderId) => {
  return draftTableRef.value?.getGenderText(genderId)
}

const onPay = () => {
  let selectedDrafts = []
  const { approvedException, waitingPayment } = lookups.draftStatus

  if (draftId.value.length === 0 || draftId.value.length >= 5) {
    notify(
      {
        group: 'error',
        title: draftId.value.length >= 5 ? i18n.t('maximumPayments') : i18n.t('CheckPayNoSelection')
      },
      6000
    )
    return
  }
  for (let index = 0; index < tableData.value?.collection?.length; index++) {
    const value = tableData.value?.collection?.[index]
    if (draftId.value.includes(value.id)) {
      if (
        value.withException &&
        value.status != approvedException &&
        value.status != waitingPayment
      ) {
        notify(
          {
            group: 'error',
            title: i18n.t('CheckPayWithException')
          },
          6000
        )
        break
      } else {
        selectedDrafts.push(value)
      }
    }
  }
  multiplePayStore.setDrafts(selectedDrafts)
  router.push('/multiplePayment')
}

const openModal = async () => {
  if (draftId.value.length === 0) {
    notify(
      {
        group: 'error',
        title: i18n.t('CheckDelete')
      },
      6000
    )
    return
  }
  showModal.value = true
}

const requestTableData = async (value) => {
  isLoading.value = true
  const payload = {
    ...value,
    readyToPaymentFlag: true
  }
  const params = {
    currentPage: initialValues.value.currentPage,
    pageSize: pageLength.value
  }

  currentSearchValues.value = value

  router.replace({ query: value })
  try {
    const response = await apiHelperService.post('/api/draft/search', payload, {
      params
    })
    if (response) {
      if (initialValues.value.requireEnrollmentInd.value === true) {
        draftStatusId.value = lookups.draftStatus.waitingFoFingerprint
      }
      isLoading.value = false
      return response
    }
  } catch (error) {
    console.error('requestTableData', error)
  }
}

const selectedNumberOfRow = async (num) => {
  let searchParams
  pageLength.value = num

  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  } else {
    searchParams = {
      ...initialValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  }

  tableData.value = await requestTableData(searchParams)
}

const onPageClick = async (pageNum) => {
  let searchParams
  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      currentPage: +pageNum
    }
  } else {
    searchParams = {
      ...initialValues.value,
      currentPage: +pageNum
    }
  }

  tableData.value = await requestTableData(searchParams)
}

const onSearch = async (value) => {
  const searchVal = value.applicantName || value.serviceBeneficiaryname || value.ownerName
  if (searchVal && !searchVal.trimStart().trimEnd().includes(' ')) {
    notify(
      {
        group: 'error',
        text: value.applicantName ? i18n.t('whiteSpace') : i18n.t('beneficiaryWhiteSpace')
      },
      6000
    )
    return
  }

  if (value.requireEnrollmentInd && authStore.isUserEstablishment) {
    value.draftStatusId = lookups.draftStatus.waitingFoFingerprint
  }
  // limit the date period to 60 days
  if (route.path.indexOf('requests-for-payment') >= 0) {
    if (value.startDate && value.endDate && isMoreThanXDays(value.startDate, value.endDate, 60)) {
      notify(
        {
          group: 'error',
          text: i18n.t('datePeriodWarning')
        },
        6000
      )
      return
    }
  }
  tableData.value = await requestTableData(value)
}

const selectedRow = (draft) => {
  if (!draft) return
  draftId.value = draft
}

const deleteSelected = (draft) => {
  draftId.value = draft
  showModal.value = true
}

const checkOut = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(searchParams)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedOutSuccessfully')
    },
    6000
  )
}

const checkIn = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(searchParams)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedInSuccessfully')
    },
    6000
  )
}

const confirmDeletionFn = async () => {
  try {
    const payload = draftId.value
    const response = await apiHelperService.post('/api/draft/delete', payload)
    if (response) {
      let searchParams = currentSearchValues.value || initialValues.value
      tableData.value = await requestTableData(searchParams)
      showModal.value = false
      notify(
        {
          group: 'success',
          title: i18n.t('Success'),
          text: i18n.t('DeletedSuccessfully')
        },
        6000
      )
    }
  } catch (error) {
    console.error('confirmDeletionFn', error)
  }
}

const setDate = () => {
  let newEndDate = DateTime.now()
  let newStartDate = newEndDate.minus({ days: 3 })

  let formattedEndDate = newEndDate.toFormat('yyyy/MM/dd')
  let formattedStartDate = newStartDate.toFormat('yyyy/MM/dd')
  initialValues.value.endDate = formattedEndDate
  initialValues.value.startDate = formattedStartDate
}

const setDraftResponse = async () => {
  if (!initialValues.value.startDate && !initialValues.value.endDate) {
    setDate()
  }
  tableData.value = await requestTableData(initialValues.value)
}

onBeforeMount(async () => {
  await setDraftResponse()
  const searchParams = route.query
  initialValues.value = searchParams
})
</script>
<style></style>
